<script setup lang="ts">
  import { File, FolderOpen, Loader2, Scale } from 'lucide-vue-next'
  import { computed } from 'vue'
  import FilePdfIcon from '@/components/icons/FilePdfIcon.vue'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: { remove: 'Remove' },
    de: { remove: 'Entfernen' },
    fr: { remove: 'Supprimer' }
  })

  const props = defineProps<{
    /** Display label (truncated automatically). */
    label: string
    /** Badge variant controls the leading icon. */
    variant: 'case' | 'pdf' | 'word' | 'other' | 'court-db'
    /** Whether the remove button is shown. Defaults to true. */
    removable?: boolean
    /** Show a spinner instead of the variant icon. */
    loading?: boolean
  }>()

  const emit = defineEmits<{
    (e: 'remove'): void
  }>()

  const isDocument = computed(() => ['pdf', 'word', 'other'].includes(props.variant))
</script>

<template>
  <!--
    Document variant: taller badge with colored icon background.

    Width is flexible: the badge sizes to its label so multiple badges share the row side
    by side and wrap to the next line when the row can't fit them. ``max-w-full`` keeps a
    badge from overflowing its row, and ``min-w-0`` lets the inner label shrink so the
    ``line-clamp-1`` truncation only kicks in when there is genuinely no more space.

    The remove affordance is overlaid on the right edge instead of taking flex space, so it
    can fade in only on hover/focus without shifting the badge's layout. ``group`` on the
    container drives the hover state of the overlay; ``focus-within`` keeps the X reachable
    for keyboard users tabbing into it.
  -->
  <div
    v-if="isDocument"
    class="group relative inline-flex h-10 min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-lg
      bg-white px-2 outline outline-1 -outline-offset-1 outline-gray-200"
  >
    <Loader2
      v-if="loading"
      class="h-6 w-6 shrink-0 animate-spin text-gray-400"
      :stroke-width="1.5"
    />
    <div
      v-else
      class="flex shrink-0 items-center overflow-hidden rounded p-1"
      :class="{
        'bg-red-100': variant === 'pdf',
        'bg-blue-100': variant !== 'pdf'
      }"
    >
      <FilePdfIcon
        v-if="variant === 'pdf'"
        class="h-4 w-4 text-gray-600"
      />
      <File
        v-else
        class="h-4 w-4 text-gray-600"
        :stroke-width="1.5"
      />
    </div>
    <span class="line-clamp-1 text-sm font-normal leading-5 text-gray-500 font-inter">
      {{ label }}
    </span>
    <!--
      Hover-only remove control. The button itself is the 24×24 box from the design
      spec — it carries the white gradient that fades the label text behind the X
      (transparent at 8.33% → white at 22.92% of its width) and the X icon is
      centered inside it (3px around an 18×18 SVG). ``right-px`` keeps the badge's
      right border visible while ``pointer-events-none`` prevents the right edge of
      the badge from accidentally triggering the remove action when the X is hidden.
    -->
    <button
      v-if="removable !== false"
      type="button"
      class="pointer-events-none absolute right-px top-1/2 flex h-6 w-6 -translate-y-1/2 items-center
        justify-center bg-[linear-gradient(90deg,rgba(255,255,255,0)_8.33%,#fff_22.92%)] opacity-0
        transition-opacity focus-visible:pointer-events-auto focus-visible:opacity-100
        group-hover:pointer-events-auto group-hover:opacity-100"
      :aria-label="t('remove')"
      @click.stop="emit('remove')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        aria-hidden="true"
        class="text-gray-800"
      >
        <path
          d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
          stroke="currentColor"
          stroke-width="1.125"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>

  <!--
    Standard variant: compact badge for case/court-db.

    Same flexible-width contract as the document variant: badges size to their label and
    wrap to the next line when the row can't fit them. ``min-w-0`` is required so the
    ``truncate`` on the label engages only when the badge actually hits the row's edge.

    Remove affordance follows the same hover-overlay pattern as the document variant —
    the X is invisible until the badge is hovered (or its remove button is focused) and
    then sits on a white gradient that fades the label behind it.
  -->
  <div
    v-else
    class="group relative inline-flex min-w-0 max-w-full items-center gap-1 overflow-hidden rounded-lg bg-white
      px-1.5 py-0.5 outline outline-1 -outline-offset-1 outline-gray-200"
  >
    <Loader2
      v-if="loading"
      class="h-4 w-4 shrink-0 animate-spin text-gray-400"
      :stroke-width="1.5"
    />
    <FolderOpen
      v-else-if="variant === 'case'"
      class="h-4 w-4 shrink-0 text-gray-500"
      :stroke-width="1"
    />
    <Scale
      v-else-if="variant === 'court-db'"
      class="h-4 w-4 shrink-0 text-gray-500"
      :stroke-width="1.5"
    />
    <span class="truncate text-sm font-normal leading-5 text-gray-500 font-inter">
      {{ label }}
    </span>
    <!--
      Smaller version of the document-variant remove control. The standard badge is
      ~24px tall, so a full 24×24 box would paint over the top, bottom and curved
      right outlines. ``h-[22px] w-[22px]`` keeps a 1px gap on every side which
      preserves the straight top/bottom/right segments of the outline.

      The right corners are rounded at 7px so the button's edge aligns exactly with
      the inside of the badge's outline curve (badge ``rounded-lg`` = 8px, ``outline``
      drawn at ``-outline-offset-1`` ⇒ outline inner edge curves at radius 7). Without
      this, the rectangular button paints over the outline at the rounded corners.
    -->
    <button
      v-if="removable !== false"
      type="button"
      class="pointer-events-none absolute right-px top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center
        justify-center rounded-r-[7px] bg-[linear-gradient(90deg,rgba(255,255,255,0)_8.33%,#fff_22.92%)]
        opacity-0 transition-opacity focus-visible:pointer-events-auto focus-visible:opacity-100
        group-hover:pointer-events-auto group-hover:opacity-100"
      :aria-label="t('remove')"
      @click.stop="emit('remove')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        aria-hidden="true"
        class="text-gray-800"
      >
        <path
          d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
          stroke="currentColor"
          stroke-width="1.125"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
</template>
